import ValidatorInterface from '../interface/Validator';
import getToken from '../CsrfToken';
import InputMask from './InputMask';
import loadIndicator from '../LoadIndicator';
import appState from '../ApplicationState';
import { sendCriteoTransaction, sendGoogle, sendYandex } from '../Analytics';
import CountryPhoneMask from './CountryPhoneMask';

const xhr = appState.axios;
const buttonsWithLoadIndicator = '.button.js-load-indicator';

export default class Submitter
{
    protected url: string;
    protected form: HTMLFormElement;
    protected validator: ValidatorInterface;
    protected params: object|Function;
    protected eventHandlers: object = {
        successSubmit: [],
    };

    constructor(form: string|HTMLFormElement, url: string, validator: ValidatorInterface)
    {
        if (typeof form === 'string') {
            form = document.getElementById(form) as HTMLFormElement;
        }

        if (form !== null && form.tagName === 'FORM') {
            this.form = form;
        }

        this.url = url;
        this.validator = validator;
    }

    public init()
    {
        this.appendEvents();
    }

    public on(eventName: string, handler: Function)
    {
        if (this.eventHandlers[eventName] instanceof Array) {
            this.eventHandlers[eventName].push(handler);
        }
    }

    /**
     * Установка дополнительных параметров для передачи в запрос
     * 
     * @param {object|Function} params - объект со списком параметров или функция, возвращающая такой объект
     */
    public setParams(params: object|Function)
    {
        this.params = params;
    }

    protected appendEvents()
    {
        this.form.addEventListener('submit', e => {
            e.preventDefault();

            if (this.validator.run(this.form)) {
                this.form.querySelectorAll(buttonsWithLoadIndicator).forEach(target => {
                    loadIndicator(target);
                });

                this.submit();
            }
        });

        this.form.querySelectorAll('input[name], textarea[name]').forEach(inputNode => {
            let input = inputNode as HTMLInputElement;
            
            input.addEventListener('focusout', () => {
                if (input.value != '') {
                    this.validator.validate(input);
                }
            });

            input.addEventListener('blur', () => {
                this.validator.message(input, '');
            });

            if (input.name === 'phone') {
                let mask = new InputMask(input, '+7 (111) 111-11-11');
                mask.startCaretPos = 4;
            } else if (input.name === 'country_phone') {
                let mask = new CountryPhoneMask(input.parentElement as HTMLElement);
            }
        });
    }

    protected submit()
    {
        let params = new FormData(this.form);
        params.append('formId', this.form.id);
        params.append('token', getToken(this.form.id));
        params.append('url', window.location.href);

        params = this.appendAdditionalParams(params);

        xhr.post(this.url, params).then(rsp => {
            if (rsp.data.status === 'success') {
                this.mutateToMessage(
                    this.anyToString(rsp.data.header, 'Спасибо!'),
                    this.anyToString(rsp.data.message, 'Сообщение успешно отправлено')
                );

                this.sendAnalytics(rsp.data);
                this.call('successSubmit');
            } else {
                this.mutateToMessage(
                    this.anyToString(rsp.data.header, 'Ошибка'),
                    this.anyToString(rsp.data.message, 'Не удалось отправить форму')
                );
            }
        }).catch(e => {
            if (e.response.status === 403) {
                this.mutateToMessage('Отклонено', 'Что-то пошло не так. Обновите страницу и попробуйте еще раз');
            } else {
                this.mutateToMessage('Ошибка', 'Не удалось отправить форму');
            }
        }).then(() => {
            this.form.querySelectorAll(buttonsWithLoadIndicator).forEach(target => {
                loadIndicator(target, true);
            });
        });
    }

    protected appendAdditionalParams(formData: FormData): FormData
    {
         let params = null;

        switch (typeof this.params) {
            case 'object':
                params = this.params;
                break;
            case 'function':
                params = this.params();
                break;
        }

        if (typeof params !== 'object') {
            return;
        }

        for (let itemName in params) {
            if (params.hasOwnProperty(itemName)) {
                formData.append(itemName, params[itemName]);
            }
        }

        return formData;
    }

    protected call(eventName: string, params: object = {})
    {
        if (this.eventHandlers[eventName] instanceof Array) {
            this.eventHandlers[eventName].forEach(handler => handler(params));
        } else {
            console.error('Submitter: unknown event');
        }
    }

    protected sendAnalytics(data: any)
    {
        let goal = this.form.dataset.yaGoal;

        if (goal) {
            sendYandex(goal);
        }

        let gaAction = this.form.dataset.gaAction;
        let gaCategory = this.form.dataset.gaCategory ? this.form.dataset.gaCategory : 'Заказ через сайт';
        let gaLabel = this.form.dataset.gaLabel ? this.form.dataset.gaLabel : null;
        let gaValue = this.form.dataset.gaValue ? this.form.dataset.gaValue : null;

        if (typeof gaAction !== 'undefined' && gaAction) {
            sendGoogle(gaCategory, gaAction, gaLabel, gaValue);
        }

        if (this.form.dataset.criteoTransaction === '1') {
            sendCriteoTransaction(data.id);
        }
    }

    /**
     * Замена верстки формы на сообщение
     */
    protected mutateToMessage(header: string = null, message: string = null)
    {
        this.form.innerHTML = '';

        if (header !== null) {
            let h2 = document.createElement('h2');
            h2.classList.add('modal__title');
            h2.innerText = header;

            let headerNode = document.createElement('div');
            headerNode.classList.add('modal__header');
            headerNode.appendChild(h2);
            
            this.form.appendChild(headerNode);
        }

        if (message !== null) {
            let body = document.createElement('div');
            body.classList.add('modal__content', 'form__hint');
            body.innerHTML = message;

            this.form.appendChild(body);
        }
    }

    private anyToString(data, defaultValue: string = null): string|null
    {
        if (typeof data === 'undefined' || data === null) {
            return defaultValue;
        }

        if (typeof data !== 'string') {
            if (typeof data.toString === 'function') {
                data = data.toString();
            } else {
                return defaultValue;
            }
        }

        return data.trim();
    }
}
